import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Upload, X, Loader2, Link, Image as ImageIcon } from 'lucide-react'
import { toast } from 'sonner'

interface MultiImageUploadProps {
  value?: string[]
  onChange: (urls: string[]) => void
  maxImages?: number
}

export function MultiImageUpload({ value = [], onChange, maxImages = 5 }: MultiImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [urlInput, setUrlInput] = useState('')

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    if (value.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`)
      return
    }

    setUploading(true)
    const uploadedUrls: string[] = []

    for (const file of files) {
      if (!file.type.startsWith('image/')) continue

      const formData = new FormData()
      formData.append('file', file)
      formData.append('upload_preset', 'Gallery')
      formData.append('cloud_name', 'dmt8z9dtk')

      try {
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/dmt8z9dtk/image/upload`,
          { method: 'POST', body: formData }
        )
        const data = await response.json()
        uploadedUrls.push(data.secure_url)
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`)
      }
    }

    onChange([...value, ...uploadedUrls])
    setUploading(false)
    toast.success(`${uploadedUrls.length} image(s) uploaded`)
  }

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return
    if (value.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`)
      return
    }
    onChange([...value, urlInput])
    setUrlInput('')
    setShowUrlInput(false)
    toast.success('Image URL added')
  }

  const removeImage = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {value.map((url, index) => (
          <div key={index} className="relative group">
            <img src={url} alt={`Product ${index + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
            <Button
              size="sm"
              variant="destructive"
              className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => removeImage(index)}
            >
              <X className="h-3 w-3" />
            </Button>
            {index === 0 && (
              <span className="absolute bottom-0.5 left-0.5 text-xs bg-primary text-primary-foreground px-1 py-0.5 rounded text-[10px]">
                Main
              </span>
            )}
          </div>
        ))}
        
        {value.length < maxImages && (
          <label className="flex flex-col items-center justify-center h-20 w-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
            />
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Upload className="h-5 w-5 text-muted-foreground mb-1" />
                <span className="text-[10px] text-muted-foreground">Upload</span>
              </>
            )}
          </label>
        )}
      </div>

      {value.length < maxImages && (
        <>
          {showUrlInput ? (
            <div className="flex gap-1">
              <Input
                placeholder="Image URL"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="text-xs h-8"
              />
              <Button size="sm" onClick={handleUrlSubmit} className="h-8 px-2">Add</Button>
              <Button size="sm" variant="outline" onClick={() => setShowUrlInput(false)} className="h-8 px-2">✕</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowUrlInput(true)} className="h-8 text-xs">
              <Link className="h-3 w-3 mr-1" />
              Add URL
            </Button>
          )}
        </>
      )}
      
      <p className="text-[10px] text-muted-foreground">
        {value.length}/{maxImages} images
      </p>
    </div>
  )
}
